import {hpcAttrs} from '@github-ui/hpc'
import {Link as PreloadableLink, Link as RouterLink, useLocation, useNavigation} from '@github-ui/react-router'
import type {Location} from '@github-ui/react-router'
import {Pagination} from '@primer/react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery, useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useRepositoryFilterActions} from '@github-ui/issues-shared/repository-filter-actions'
import {IssuesList} from '@github-ui/shared-dashboard-lists/issues/IssuesList'
import type {FilterSelectType} from '@github-ui/shared-dashboard-lists/shared/types/list-types'
import type {EmptyListState} from '@github-ui/shared-dashboard-lists/shared/types/blankslate-types'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import {useCallback, useMemo, useState} from 'react'

import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {addStateToQuery, getStateFromQuery} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {addIssueFieldToQuery, createReplaceIssueFieldQuery} from '@github-ui/issue-url-helper'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addBlockedFilterToQuery,
  addLabelFilterToQuery,
  addParentIssueFilterToQuery,
  addRepoFilterToQuery,
  addTypeFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'

import {useIssuesIndexResults} from '../hooks/use-issues-index-results'
import {repoIssuesToListItems} from '../helpers/repo-issues-dashboard-proto'
import {IssuesIndexSearch} from './IssuesIndexSearch'
import {issuesIndexContentRoute, issuesLabelContentRoute} from './issues-index-content-route'
import {issuesLayoutRoute} from './issues-layout-route'

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

/**
 * Repo Issues#index content: a repo analog of `IssuesDashboardSurfaceContent` that
 * reuses the shared `IssuesList`, pagination, skeleton/blankslate, and a repo-scoped
 * `<Filter>` to render the mixed (issues + PRs) result list.
 */
export function IssuesIndexContent() {
  const navigation = useNavigation()
  const location = useLocation()
  const isNavigating = Boolean(navigation.location && !isSameLocation(navigation.location, location))

  return (
    <>
      <IssuesIndexSearch />
      <ErrorBoundary
        boundaryName="IssuesIndexContent"
        fallback={
          <div className="tmp-my-3">
            <SharedDashboardBlankslate dashboardType="issues" variant="error" />
          </div>
        }
      >
        <IssuesIndexPageContent isNavigating={isNavigating} />
      </ErrorBoundary>
    </>
  )
}

function IssuesIndexPageContent({isNavigating}: {isNavigating: boolean}) {
  const [compactMode, setCompactMode] = useState(false)
  const location = useLocation()
  const contentRoute = location.pathname.split('/')[3] === 'labels' ? issuesLabelContentRoute : issuesIndexContentRoute

  const {data: layoutPayload} = useRouteQuery(issuesLayoutRoute, 'mainQuery')
  const {data} = useSuspenseRouteQuery(contentRoute, 'mainQuery')
  const {owner, repo} = useRouteParams(contentRoute)
  const {fetchResults, fetchedQuery, getPageHref, getQueryHref} = useIssuesIndexResults()
  const scopedRepository = useMemo(() => ({owner, name: repo}), [owner, repo])

  const applySectionFilter = useCallback(
    (query: string) => {
      fetchResults({q: query})
    },
    [fetchResults],
  )
  const filterActions = useRepositoryFilterActions({
    repo: scopedRepository,
    query: fetchedQuery,
    applySectionFilter,
    issueTypesEnabled: layoutPayload?.issue_types_enabled ?? false,
  })

  const issues = useMemo(() => repoIssuesToListItems(data.results), [data.results])
  const pageInfo = data.pageInfo ?? {currentPage: 1, totalPages: 1, totalCount: 0}
  const stateCounts =
    data.pageInfo?.openCount === undefined || data.pageInfo.closedCount === undefined
      ? undefined
      : {open: data.pageInfo.openCount, closed: data.pageInfo.closedCount}
  const error = data.error || null

  const handleFilterSelect = (type: FilterSelectType, value: string) => {
    switch (type) {
      case 'repo':
        return fetchResults({q: addRepoFilterToQuery(fetchedQuery, value)})
      case 'author':
        return fetchResults({q: addAuthorFilterToQuery(fetchedQuery, value)})
      case 'label':
        return fetchResults({q: addLabelFilterToQuery(fetchedQuery, value)})
      case 'assignee':
        return fetchResults({q: addAssigneeFilterToQuery(fetchedQuery, value)})
      default: {
        const _exhaustive: never = type
        return _exhaustive
      }
    }
  }

  const handleIssueFieldSelect = (fieldName: string, queryValue: string) => {
    const newQuery =
      createReplaceIssueFieldQuery(fetchedQuery, fieldName, queryValue) ??
      addIssueFieldToQuery(fetchedQuery, fieldName, queryValue)
    fetchResults({q: newQuery})
  }

  const emptyListState: EmptyListState = {
    dashboardType: 'issues',
    filtered: fetchedQuery.length > 0,
    currentPage: pageInfo.currentPage,
    pageCount: pageInfo.totalPages,
    errorMessage: error ?? undefined,
  }

  if (error) {
    return (
      <div className="tmp-my-3">
        <SharedDashboardBlankslate dashboardType="issues" variant="error" />
      </div>
    )
  }

  return (
    <div className="tmp-my-3">
      <div {...hpcAttrs()}>
        <IssuesList
          issues={issues}
          pageInfo={pageInfo}
          compactMode={compactMode}
          onCompactModeChange={setCompactMode}
          defaultSortingKey={layoutPayload?.best_match_sort_enabled ? 'relevance' : 'created'}
          onSortingItemSelect={sort => fetchResults({q: addSortToQuery(fetchedQuery, sort)})}
          onStateFilterSelect={state => fetchResults({q: addStateToQuery(fetchedQuery, state)})}
          sortingItemSelected={getSortFromQuery(fetchedQuery)}
          stateItemSelected={getStateFromQuery(fetchedQuery)}
          stateCounts={stateCounts}
          emptyListState={emptyListState}
          onFilterSelect={handleFilterSelect}
          onIssueFieldSelect={handleIssueFieldSelect}
          filterActions={filterActions}
          isLoading={isNavigating}
          isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
          getIssueTypeHref={issueTypeName => getQueryHref(addTypeFilterToQuery(fetchedQuery, issueTypeName))}
          getIssueBlockedHref={() => getQueryHref(addBlockedFilterToQuery(fetchedQuery))}
          getParentIssueHref={(repoNameWithOwner, number) =>
            getQueryHref(addParentIssueFilterToQuery(fetchedQuery, repoNameWithOwner, number))
          }
          issueTypeLinkComponent={RouterLink}
        />
        {pageInfo.totalPages > 1 && (
          <Pagination
            pageCount={pageInfo.totalPages}
            currentPage={pageInfo.currentPage}
            hrefBuilder={getPageHref}
            renderPage={({key, number, href, ...props}) => (
              <PreloadableLink key={key} {...props} to={href ?? getPageHref(number)} preload="intent" />
            )}
            onPageChange={(e, page) => {
              e.preventDefault()
              fetchResults({q: fetchedQuery, page})
            }}
          />
        )}
      </div>
    </div>
  )
}
